import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { User } from '../common/models/user';
import { AuthService as AuthApiService } from '../../api/services';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser: Observable<User | null>;

  constructor(private readonly authService: AuthApiService) {
    let storageUser = null;
    try {
      storageUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
    } catch (error) {
      // if we can't access local storage continue
    }
    this.currentUserSubject = new BehaviorSubject<User | null>(storageUser);
    this.currentUser = this.currentUserSubject.asObservable();
    this.removeFromStorageIfExpired(storageUser);
  }

  removeFromStorageIfExpired(currentUser: User) {
    if (!currentUser || !currentUser.tokenExpiresAt || new Date(currentUser.tokenExpiresAt) < new Date()) {
      localStorage.removeItem('currentUser');
      this.currentUserSubject.next(null);
    }
  }

  public get currentUserValue(): User | null {
    const currentUser = this.currentUserSubject.value;
    if (currentUser && currentUser.tokenExpiresAt) {
      this.removeFromStorageIfExpired(currentUser);
    }
    return this.currentUserSubject.value;
  }

  login(email: string, password: string) {
    return this.authService.authControllerLogin({ body: { email, password } }).pipe(
      map((response) => {
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        const user: User = {
          ...response.user,
          token: response.accessToken,
          tokenExpiresAt: new Date(new Date().getTime() + response.expiresIn * 1000).toISOString(),
        };
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.currentUserSubject.next(user);
        return user;
      }),
    );
  }

  async logout() {
    await lastValueFrom(this.authService.authControllerLogout());
    // remove user from local storage and set current user to null
    localStorage.removeItem('currentUser');
    this.currentUserSubject.next(null);
  }
}
